<script setup lang="ts">
import { ref } from 'vue'
import { useRouter } from 'vue-router'
import { ApiRequestError, changePassword, logout } from '@/api/client'
import { useAuthStore } from '@/stores/auth'
import { ChangePasswordSchema, firstIssue } from '@/validation'

const router = useRouter()
const auth = useAuthStore()
const currentPassword = ref('')
const newPassword = ref('')
const confirmPassword = ref('')
const busy = ref(false)
const error = ref('')
const signingOut = ref(false)

async function signOut() {
  error.value = ''
  signingOut.value = true
  try {
    await logout()
    auth.clearAccount()
    await router.replace('/login')
  } catch (cause) {
    error.value =
      cause instanceof ApiRequestError ? cause.message : 'Unable to sign out. Please try again.'
  } finally {
    signingOut.value = false
  }
}
async function updatePassword() {
  error.value = ''
  if (newPassword.value !== confirmPassword.value) {
    error.value = 'New passwords do not match.'
    return
  }
  const issue = firstIssue(ChangePasswordSchema.safeParse({ newPassword: newPassword.value }))
  if (issue !== undefined) {
    error.value = issue
    return
  }
  busy.value = true
  try {
    await changePassword(currentPassword.value, newPassword.value)
    auth.clearAccount()
    await router.replace({ name: 'login', query: { changed: '1' } })
  } catch (cause) {
    error.value =
      cause instanceof ApiRequestError ? cause.message : 'Unable to change your password.'
  } finally {
    busy.value = false
  }
}
</script>

<template>
  <main class="admin-page">
    <header class="admin-header">
      <p class="eyebrow">Account</p>
      <h1 class="display">Your account</h1>
    </header>
    <section class="admin-card account-summary" aria-label="Account details">
      <dl>
        <div>
          <dt>Email</dt>
          <dd>{{ auth.account?.email }}</dd>
        </div>
        <div>
          <dt>Role</dt>
          <dd>{{ auth.account?.role }}</dd>
        </div>
      </dl>
      <RouterLink v-if="auth.isAdmin" class="btn btn-primary" to="/admin/intakes"
        >Manage intake links</RouterLink
      >
      <p v-else class="account-note">
        Your account is active, but intake administration requires administrator access.
      </p>
      <button class="btn btn-secondary" type="button" :disabled="signingOut" @click="signOut">
        {{ signingOut ? 'Signing out…' : 'Sign out' }}
      </button>
    </section>
    <section
      v-if="auth.account?.authentication_methods.includes('password')"
      class="admin-card password-card"
      aria-labelledby="password-title"
    >
      <h2 id="password-title">Change password</h2>
      <p>Changing your password signs out every active session.</p>
      <form novalidate @submit.prevent="updatePassword">
        <label
          >Current password
          <input
            v-model="currentPassword"
            type="password"
            autocomplete="current-password"
            required
            :disabled="busy"
        /></label>
        <label
          >New password
          <input
            v-model="newPassword"
            type="password"
            autocomplete="new-password"
            minlength="10"
            required
            :disabled="busy"
        /></label>
        <label
          >Confirm new password
          <input
            v-model="confirmPassword"
            type="password"
            autocomplete="new-password"
            required
            :disabled="busy"
        /></label>
        <p v-if="error" class="form-error" role="alert">{{ error }}</p>
        <button class="btn btn-primary" :disabled="busy">
          {{ busy ? 'Changing password…' : 'Change password' }}
        </button>
      </form>
    </section>
  </main>
</template>
